import { useMemo } from 'react';

import {
  Column,
  ColumnOrderState,
  OnChangeFn,
  Table,
  VisibilityState,
} from '@tanstack/react-table';
import { Columns2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

import { ReorderableColumn, ReorderableColumnSelector } from './reorderable-column-selector';

interface DataTableColumnsSelectorProps<TData> {
  /** The TanStack table instance (must have columnOrder state wired, e.g. via useDataTable). */
  table: Table<TData>;
  /**
   * Controlled column order + visibility (with setters), for pages that own this state and
   * feed it into a separately-rendered `useReactTable` (e.g. time-tracking approvals). There,
   * `table.getState()` lags a render behind the page's state, so the picker would snap back or
   * flip a click late. Passing the page's own state makes the picker read/write it directly.
   * Omit for `useDataTable` tables — they have no such split.
   */
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
}

/** Human-readable label for a column, or null when it has none (skip such columns). */
const getColumnLabel = <TData,>(column: Column<TData, unknown>): string | null => {
  const header = column.columnDef.header;
  if (typeof header === 'string') return header;
  return column.columnDef.meta?.title ?? null;
};

const toReorderableColumn = <TData,>(
  column: Column<TData, unknown>,
  isVisible: (column: Column<TData, unknown>) => boolean
): ReorderableColumn => ({
  key: column.id,
  label: getColumnLabel(column) as string,
  checked: isVisible(column),
  disabled: !column.getCanHide(), // non-hideable → locked checkbox
});

/**
 * The standard column selector for every TanStack `<DataTable>`: a popover picker to show/hide
 * and drag-to-reorder columns, driving the table's own `columnVisibility` + `columnOrder`.
 * Supersedes the legacy visibility-only `DataTableColumnsDropdown`.
 *
 * Only labeled columns appear (unlabeled ones — selection checkbox, actions — are skipped).
 * Pinned columns always paint at the table edge, so they can't be reordered: they go to a
 * non-draggable "fixed" section instead. Reorder always commits the FULL leaf-column id list
 * so no column is dropped.
 */
export function DataTableColumnsSelector<TData>({
  table,
  columnOrder: controlledColumnOrder,
  onColumnOrderChange,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
}: DataTableColumnsSelectorProps<TData>) {
  const { t } = useTranslation();

  // Prefer controlled state when provided (avoids the split-render lag; see prop docs).
  const tableState = table.getState();
  const columnVisibility = controlledColumnVisibility ?? tableState.columnVisibility;
  const columnOrder = controlledColumnOrder ?? tableState.columnOrder;

  const isColumnVisible = (column: Column<TData, unknown>): boolean =>
    controlledColumnVisibility
      ? controlledColumnVisibility[column.id] !== false // absent from map = visible
      : column.getIsVisible();

  // Re-sort against `columnOrder`: `getAllLeafColumns()` uses the table's own order, which can
  // lag the controlled value by a render, so the picker would mismatch the rendered table.
  const leafColumns = useMemo(() => {
    const all = table.getAllLeafColumns();
    if (!columnOrder.length) return all;
    const byId = new Map(all.map(column => [column.id, column]));
    const ordered = columnOrder
      .map(id => byId.get(id))
      .filter((c): c is Column<TData, unknown> => !!c);
    // Append any columns not present in columnOrder (matches TanStack's own leftover handling).
    const seen = new Set(ordered.map(c => c.id));
    return [...ordered, ...all.filter(c => !seen.has(c.id))];
  }, [table, columnOrder]);

  const { reorderableColumns, fixedColumns } = useMemo(() => {
    // Fixed = pinned in the table, or opted in via `meta.fixedInSelector` (picker-only).
    const isFixed = (column: Column<TData, unknown>) =>
      column.getIsPinned() !== false || column.columnDef.meta?.fixedInSelector === true;

    const labeled = leafColumns.filter(column => getColumnLabel(column) !== null);
    // `position` is the 1-based slot in the full labeled order, counting hidden columns.
    const pickerColumns = labeled.map(
      (column, index): ReorderableColumn => ({
        ...toReorderableColumn(column, isColumnVisible),
        position: index + 1,
      })
    );

    // pickerColumns is 1:1 with labeled, so the index filter picks the right entries.
    return {
      reorderableColumns: pickerColumns.filter((_, index) => !isFixed(labeled[index])),
      fixedColumns: pickerColumns.filter((_, index) => isFixed(labeled[index])),
    };
    // isColumnVisible is derived from columnVisibility, which is in the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafColumns, columnVisibility]);

  // When a controlled setter is provided, write to it; otherwise write to the table directly.
  const handleToggle = (key: string) => {
    const column = table.getColumn(key);
    if (!column?.getCanHide()) return;

    const nextVisible = !isColumnVisible(column);
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(prev => ({ ...prev, [key]: nextVisible }));
    } else {
      column.toggleVisibility(nextVisible);
    }
  };

  const handleReorder = (orderedReorderableKeys: string[]) => {
    // Keep fixed/unlabeled columns in place, slot the dragged ones into their new sequence,
    // and commit the full order so no column is dropped.
    const reorderableKeySet = new Set(orderedReorderableKeys);
    let nextReorderableIndex = 0;

    const nextOrder = leafColumns.map(column => {
      if (reorderableKeySet.has(column.id)) {
        const key = orderedReorderableKeys[nextReorderableIndex];
        nextReorderableIndex += 1;
        return key;
      }
      return column.id;
    });

    if (onColumnOrderChange) {
      onColumnOrderChange(nextOrder);
    } else {
      table.setColumnOrder(nextOrder);
    }
  };

  return (
    <ReorderableColumnSelector
      columns={reorderableColumns}
      onToggle={handleToggle}
      onReorder={handleReorder}
      pinnedColumns={fixedColumns.length > 0 ? fixedColumns : undefined}
      pinnedDividerLabel={fixedColumns.length > 0 ? t('common:table.fixedColumns') : undefined}
      reorderHandleLabel={t('common:table.reorderColumn')}
      align="end"
      contentClassName="w-64"
      trigger={
        <Button variant="outline" className="h-9 rounded border px-2 py-1 text-left text-sm">
          <Columns2 />
          {t('common:table.customizeColumns')}
        </Button>
      }
    />
  );
}
